import {
  CALENDAR_PENDING_POLL_MS,
  calendarWorkPending
} from '@Integrations/components/calendar/calendarConnectionTypes';
import { PageAuthContext } from '@Pages/pageRuntime/context/PageAuthContext';
import { usePageRuntime } from '@Pages/pageRuntime/pageRuntimeContextValue';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkCalendarChoices,
  SdkCalendarConnectionsHandle,
  SdkCalendarLink,
  SdkCalendarLogin,
  SdkCalendarResult,
  SdkCalendarSignInResult,
  SdkCalendarStaff,
  SdkCalendarUnavailableReason,
  SdkUseCalendarConnectionsOptions
} from '@Pages/pageRuntime/sdk/types';
import axios from 'axios';
import { useContext } from 'react';

import { useApi } from './useApi';

export const CALENDAR_CONNECTIONS_URL = '/api/calendar-connections';

type CalendarConnectionsResponse = {
  available: boolean;
  reason: Exclude<SdkCalendarUnavailableReason, 'signed_out'> | null;
  configured: boolean;
  staff: SdkCalendarStaff | null;
  can_manage_others: boolean;
  links: SdkCalendarLink[];
  logins: SdkCalendarLogin[];
  result: SdkCalendarSignInResult | null;
};

type Target = { resource_id?: string; record_id?: string };

const NO_LINKS: SdkCalendarLink[] = [];
const NO_LOGINS: SdkCalendarLogin[] = [];

function target(options?: SdkUseCalendarConnectionsOptions): Target {
  return options?.resourceId && options.recordId
    ? { resource_id: options.resourceId, record_id: options.recordId }
    : {};
}

function send<T>(request: Promise<{ data: T }>): Promise<T> {
  return request.then(
    (response) => response.data,
    (error: unknown) => {
      throw toApiError(error);
    }
  );
}

export function useCalendarConnections(
  options?: SdkUseCalendarConnectionsOptions
): SdkCalendarConnectionsHandle {
  const sdk = usePageRuntime()?.Sdk;
  const currentPageId = useContext(PageAuthContext)?.currentPageId ?? null;
  const signedIn = (sdk?.identity ?? null) !== null;
  const params = target(options);
  const query = useApi(
    ['calendar-connections', params],
    (api) =>
      api.get<CalendarConnectionsResponse>(CALENDAR_CONNECTIONS_URL, {
        params
      }),
    {
      enabled: signedIn,
      // A disconnect removes events and a reconnect checks access in the background, so the
      // handle refreshes until they finish.
      pollInterval: (current) =>
        current !== undefined && calendarWorkPending(current.links, current.logins)
          ? CALENDAR_PENDING_POLL_MS
          : false
    }
  );
  const data = signedIn ? query.data : undefined;

  // A refusal can mean the link or the account changed elsewhere, so every write refreshes.
  function write<T>(request: Promise<{ data: T }>): Promise<T> {
    return send(request).finally(() => void query.invalidate());
  }

  async function startSignIn(url: string): Promise<void> {
    const { authorize_url } = await send(
      axios.post<{ authorize_url: string }>(url, {
        ...params,
        current_page_id: currentPageId
      })
    );
    window.location.assign(authorize_url);
  }

  return {
    available: data?.available ?? false,
    reason: signedIn ? (data?.reason ?? null) : 'signed_out',
    configured: data?.configured ?? false,
    staff: data?.staff ?? null,
    links: data?.links ?? NO_LINKS,
    logins: data?.logins ?? NO_LOGINS,
    canManageOthers: data?.can_manage_others ?? false,
    result: data?.result ?? null,
    isLoading: signedIn && query.isLoading,
    error: signedIn ? query.error : null,
    connect: () => startSignIn(`${CALENDAR_CONNECTIONS_URL}/logins`),
    reconnect: (loginId) => startSignIn(`${CALENDAR_CONNECTIONS_URL}/logins/${loginId}/reconnect`),
    disconnect: (loginId, options) =>
      write(
        axios.post<SdkCalendarResult>(`${CALENDAR_CONNECTIONS_URL}/logins/${loginId}/disconnect`, {
          ...params,
          calendar_events: options?.events ?? 'keep'
        })
      ),
    listChoices: (loginId) =>
      send(
        axios.get<SdkCalendarChoices>(`${CALENDAR_CONNECTIONS_URL}/choices`, {
          params: {
            ...params,
            ...(loginId ? { login_id: loginId } : {})
          }
        })
      ),
    addLink: (input) =>
      write(
        axios.post<SdkCalendarResult>(`${CALENDAR_CONNECTIONS_URL}/links`, {
          ...params,
          login_id: input.loginId,
          ...(input.newCalendar
            ? { new_calendar: true }
            : input.email === undefined
              ? { calendar_id: input.calendarId }
              : { email: input.email }),
          receives_events: input.receivesEvents,
          blocks_availability: input.blocksAvailability
        })
      ),
    updateLink: (linkId, flags, options) =>
      write(
        axios.patch<SdkCalendarResult>(`${CALENDAR_CONNECTIONS_URL}/links/${linkId}`, {
          receives_events: flags.receivesEvents,
          blocks_availability: flags.blocksAvailability,
          calendar_events: options?.events ?? 'keep'
        })
      ),
    removeLink: (linkId, options) =>
      write(
        axios.delete<SdkCalendarResult>(`${CALENDAR_CONNECTIONS_URL}/links/${linkId}`, {
          data: { calendar_events: options?.events ?? 'keep' }
        })
      )
  };
}
